import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, CheckCircle } from 'lucide-react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import termsService from '../services/termsService';
import './TermsPage.css';

const TermsPage = () => {
  const [termsData, setTermsData] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [acceptanceHistory, setAcceptanceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('terms');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [terms, agreement, history] = await Promise.all([
        termsService.getCurrentTerms().catch(() => null),
        termsService.getCurrentServiceAgreement().catch(() => null),
        termsService.getAcceptanceHistory({ page: 1, limit: 10 }).catch(() => ({ results: [] }))
      ]);

      setTermsData(terms);
      setAgreementData(agreement);
      setAcceptanceHistory(history.results || []);
    } catch (error) {
      console.error('Error fetching terms data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="terms-page">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="terms-page">
        <div className="terms-page-container">
          <div className="terms-page-header">
            <button onClick={() => navigate(-1)} className="back-button">
              <ArrowLeft size={20} />
            </button>
            <h1>Terms & Policies</h1>
          </div>

          <div className="terms-tabs">
            <button 
              className={`tab-button ${activeTab === 'terms' ? 'active' : ''}`}
              onClick={() => setActiveTab('terms')}
            >
              <FileText size={18} />
              Terms & Conditions
            </button>
            <button 
              className={`tab-button ${activeTab === 'agreement' ? 'active' : ''}`}
              onClick={() => setActiveTab('agreement')}
            >
              <FileText size={18} />
              Service Agreement
            </button>
            <button 
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              <Calendar size={18} />
              Acceptance History
            </button>
          </div>

          <div className="terms-tab-content">
            {activeTab === 'terms' && (
              <div className="terms-document">
                {termsData ? (
                  <>
                    <div className="document-header">
                      <h2>Terms & Conditions</h2>
                      <div className="document-meta">
                        <span className="version-badge">Version {termsData.version}</span>
                        <span className="date-info">
                          Effective from {formatDate(termsData.effective_date)}
                        </span>
                      </div>
                    </div>
                    <div 
                      className="document-content"
                      dangerouslySetInnerHTML={{ __html: termsData.content }}
                    />
                  </>
                ) : (
                  <div className="no-data">
                    <p>Terms & Conditions not available</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'agreement' && (
              <div className="terms-document">
                {agreementData ? (
                  <>
                    <div className="document-header">
                      <h2>Service Agreement</h2>
                      <div className="document-meta">
                        <span className="version-badge">Version {agreementData.version}</span>
                        <span className="date-info">
                          Effective from {formatDate(agreementData.effective_date)}
                        </span>
                      </div>
                    </div>
                    <div 
                      className="document-content"
                      dangerouslySetInnerHTML={{ __html: agreementData.content }}
                    />
                  </>
                ) : (
                  <div className="no-data">
                    <p>Service Agreement not available</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="acceptance-history">
                <h2>Your Acceptance History</h2>
                <p className="history-description">
                  Track when you accepted different versions of our terms and agreements
                </p>

                {acceptanceHistory.length > 0 ? (
                  <div className="history-list">
                    {acceptanceHistory.map((record) => (
                      <div key={record.id} className="history-item">
                        <div className="history-icon">
                          <CheckCircle size={24} />
                        </div>
                        <div className="history-details">
                          <div className="history-type">
                            {record.document_type === 'terms' && 'Terms & Conditions'}
                            {record.document_type === 'agreement' && 'Service Agreement'}
                            {record.document_type === 'both' && 'Terms & Service Agreement'}
                          </div>
                          <div className="history-version">
                            Version {record.version}
                          </div>
                          <div className="history-date">
                            Accepted on {formatDateTime(record.accepted_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-data">
                    <CheckCircle size={48} color="#d1d5db" />
                    <p>No acceptance history found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TermsPage;
